import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Box, Typography, Stack, Skeleton, Button, Divider, Slider, Tooltip,
} from '@mui/material';
import ArrowUpwardRoundedIcon    from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon  from '@mui/icons-material/ArrowDownwardRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import { SparkLineChart }        from '@mui/x-charts/SparkLineChart';
import { areaElementClasses }    from '@mui/x-charts/LineChart';
import { useGrowSetup }          from '../../GrowSetupContext';
import { AuthContext }           from '../../components/signin/AuthContext';
import { useAlert }              from '../../components/useAlert';
import { useConfig }             from '../../components/ConfigContext';
import SmallConfigLicht          from '../../components/SmallConfigLicht';
import SmallConfigAbluft         from '../../components/SmallConfigAbluft';
import SmallConfigTemperatur     from '../../components/SmallConfigTemperatur';
import { Modal } from '@mui/material';

const TREND_META = {
  up:      { color: '#22c55e', Icon: ArrowUpwardRoundedIcon    },
  down:    { color: '#ef4444', Icon: ArrowDownwardRoundedIcon  },
  neutral: { color: '#64748b', Icon: HorizontalRuleRoundedIcon },
};

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
        <stop offset="100%" stopColor={color} stopOpacity={0}    />
      </linearGradient>
    </defs>
  );
}

function ModeButton({ label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2, py: 0.6, borderRadius: '20px', cursor: 'pointer',
        fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em',
        transition: 'all 0.18s ease',
        background: active ? 'linear-gradient(135deg,#76c442 0%,#559e2e 100%)' : 'rgba(255,255,255,0.05)',
        color:      active ? '#000' : 'rgba(255,255,255,0.5)',
        border:     active ? '1px solid #76c442' : '1px solid rgba(255,255,255,0.1)',
        boxShadow:  active ? '0 2px 10px rgba(118,196,66,0.35)' : 'none',
        userSelect: 'none',
        '&:hover': { opacity: 0.88 },
      }}
    >
      {label}
    </Box>
  );
}

export default function ActorCard({ measure, title, trend, animDelay = 0 }) {
  const { grow_setup }  = useGrowSetup();
  const { token }       = useContext(AuthContext);
  const showAlert       = useAlert();
  const { state: cfgState } = useConfig();
  const configReady = typeof cfgState.config.sun_rise === 'string';

  const [chartData, setChartData] = useState([]);
  const [xaxis,     setXaxis]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [isOn,      setIsOn]      = useState(false);
  const [mode,      setMode]      = useState('auto');
  const [intensity, setIntensity] = useState(0);
  const [open,      setOpen]      = useState(false);

  const trendMeta  = TREND_META[trend] ?? TREND_META.neutral;
  const gradientId = useMemo(() => `v3-actor-grad-${measure}`, [measure]);
  const cardColor  = isOn || mode === 'auto' ? '#22c55e' : '#ef4444';

  useEffect(() => {
    if (!grow_setup) return;
    let cancelled = false;
    setLoading(true);
    const end   = new Date();
    const start = new Date(end.getTime() - 24 * 3600 * 1000);
    Promise.all([
      axios.post('/get_measures_by_timedelta/', {
        start_time: start, end_time: end,
        grow_setup_id: grow_setup, aggregation_level: '5m', measure,
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }),
      axios.post('/get_actor_data', { grow_setup_id: grow_setup, measure }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }),
    ]).then(([measRes, actorRes]) => {
      if (cancelled) return;
      const row  = measRes?.data?.[0];
      const data = row?.data  ?? [];
      const rawX = row?.xaxis ?? [];
      const fmt  = ts => { const d = new Date(ts); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
      setChartData(data);
      setXaxis(rawX.map(fmt));
      if (actorRes?.data) {
        const m = actorRes.data.mode;
        const v = Number(actorRes.data.value);
        setMode(m ?? 'auto');
        setIsOn(m === 'on');
        setIntensity(Number.isFinite(v) ? v : 0);
      }
    }).catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [grow_setup, measure]);

  const handleMode = async (m) => {
    try {
      await axios.post('/set_actor_mode', { id: grow_setup, actor: measure, mode: m }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setMode(m); setIsOn(m === 'on');
      showAlert('Modus geändert', 'success');
    } catch { showAlert('Fehler beim Ändern des Modus', 'error'); }
  };

  const handleIntensity = async (v) => {
    try {
      await axios.post('/set_actor_intensity', { id: grow_setup, actor: measure, intensity: v }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setIntensity(v); setIsOn(true);
      showAlert('Intensität gespeichert', 'success');
    } catch { showAlert('Fehler beim Speichern', 'error'); }
  };

  const pulseKf = `
    @keyframes actorPulse_${measure} {
      0%,100% { transform:scale(1); opacity:1; }
      50%      { transform:scale(0.85); opacity:0.6; }
    }`;

  return (
    <>
      <style>{`
        ${pulseKf}
        @keyframes fadeSlideUpCard { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <Box
        sx={{
          borderRadius: '16px',
          border: `1px solid ${cardColor}33`,
          background: 'linear-gradient(145deg,rgba(15,28,15,0.9) 0%,rgba(8,18,10,0.85) 100%)',
          backdropFilter: 'blur(16px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxShadow: `0 4px 24px ${cardColor}22, inset 0 0 0 1px ${cardColor}1a`,
          animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 32px ${cardColor}33` },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                {title || measure}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>
                  Aktor
                </Typography>
                <trendMeta.Icon fontSize="small" sx={{ color: trendMeta.color }} />
              </Stack>
            </Box>

            {/* Power indicator */}
            <Box sx={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{
                position: 'absolute', width: '100%', height: '100%',
                borderRadius: '50%',
                background: `${cardColor}22`,
                animation: (isOn || mode === 'auto') ? `actorPulse_${measure} 2s infinite` : 'none',
              }} />
              <PowerSettingsNewRoundedIcon sx={{ color: cardColor, fontSize: 24, filter: `drop-shadow(0 0 6px ${cardColor})` }} />
            </Box>
          </Stack>
        </Box>

        {/* Status bar */}
        <Box sx={{ height: 2, background: `linear-gradient(90deg,${cardColor} 0%,${cardColor}00 100%)`, mx: 2.5, borderRadius: 1, mb: 1 }} />

        {/* Mode Buttons */}
        <Box sx={{ px: 2.5, pb: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ModeButton label="Auto" active={mode === 'auto'} onClick={() => handleMode('auto')} />
            <ModeButton label="On"   active={mode === 'on'}   onClick={() => handleMode('on')}   />
            <ModeButton label="Off"  active={mode === 'off'}  onClick={() => handleMode('off')}  />
          </Stack>
        </Box>

        {/* Intensity Slider */}
        {(isOn || mode === 'on') && (
          <Box sx={{ px: 2.5, pb: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
                {intensity}%
              </Typography>
              <Slider
                value={intensity}
                min={0} max={100} step={5}
                size="small"
                onChange={(_, v) => setIntensity(Number(v))}
                onChangeCommitted={(_, v) => handleIntensity(Number(v))}
                sx={{
                  color: '#76c442',
                  '& .MuiSlider-thumb': {
                    width: 14, height: 14,
                    '&:hover': { boxShadow: '0 0 0 8px rgba(118,196,66,0.16)' },
                  },
                  '& .MuiSlider-rail': { opacity: 0.2 },
                }}
              />
            </Stack>
          </Box>
        )}

        {/* Chart */}
        <Box sx={{ px: 2.5, flex: 1, minHeight: 65, mt: 0.5 }}>
          {loading ? (
            <Skeleton variant="rounded" height={55} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
          ) : (
            <Box sx={{ width: '100%', height: 65 }}>
              <SparkLineChart
                data={chartData}
                xAxis={{ scaleType: 'band', data: xaxis }}
                colors={[cardColor]}
                area showHighlight showTooltip
                sx={{
                  [`& .${areaElementClasses.root}`]: { fill: `url(#${gradientId})` },
                  '& .MuiChartsAxis-root': { display: 'none' },
                }}
              >
                <AreaGradient color={cardColor} id={gradientId} />
              </SparkLineChart>
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, mt: 1 }} />

        <Box sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => setOpen(true)}
            sx={{ color: '#76c442', fontWeight: 600, fontSize: '0.75rem', '&:hover': { background: 'rgba(118,196,66,0.1)' } }}
          >
            Konfigurieren
          </Button>
        </Box>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 440, maxWidth: '92vw',
          background: 'rgba(8,20,9,0.97)',
          border: '1px solid rgba(118,196,66,0.2)',
          borderRadius: '18px',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          p: 3,
        }}>
          {configReady && measure === 'licht'      && <SmallConfigLicht />}
          {configReady && measure === 'abluft'     && <SmallConfigAbluft />}
          {configReady && measure === 'temperatur' && <SmallConfigTemperatur />}
          {configReady && !['licht','abluft','temperatur'].includes(measure) && (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', py: 2 }}>Keine Konfiguration verfügbar.</Typography>
          )}
          {!configReady && <Typography sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 4 }}>Laden…</Typography>}
          <Button fullWidth variant="outlined" onClick={() => setOpen(false)} sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>
            Schließen
          </Button>
        </Box>
      </Modal>
    </>
  );
}

ActorCard.propTypes = {
  measure:   PropTypes.string.isRequired,
  title:     PropTypes.string,
  trend:     PropTypes.oneOf(['up','down','neutral']),
  animDelay: PropTypes.number,
};
